<template>
  <!-- display:contents: wrapper puramente para darle a este SFC una única
       raíz (Vue no permite v-for sobre la raíz de un componente con
       estado); no debe generar caja propia — los ListPlegable deben seguir
       comportándose como hermanos directos del resto de las filas de
       Layers.vue, igual que antes de este wrapper. -->
  <div class="terrain-section-root">
    <ListPlegable
      v-for="(category, i) in filteredCategories"
      :key="category.id"
      :category="category"
      :index="i"
      :check-state="categoryCheckState(category)"
      :search-query="isSearchActive ? searchQuery : ''"
      :is-search-active="isSearchActive"
      @selectLayer="onSelectLayer"
      @removeLayer="onRemoveLayer"
      @toggleCategory="onToggleCategory"
      @updateOpacity="onUpdateOpacity"
      @zoomLayer="onZoomLayer"
    />
  </div>
</template>

<script>
// TerrainSection.vue — sección hermana de la lista de capas para Capas_
// Terreno (tarea 13.2, Requirement 18.1-18.4): NO se integra como una
// categoría más del catálogo GeoServer/ArcGIS existente (ver design.md,
// finding #7/#10), sino como un bloque propio con orden administrativo. Usa
// ListPlegable en su modo NORMAL (no "externo" — ese es para capas WMS de
// sesión, sin tabla de atributos y con opción de eliminar, ninguna de las
// dos aplica acá) para que una Capa_Terreno se vea y se comporte igual que
// el resto de las capas del catálogo: mismo checkbox/opacidad/zoom/tabla de
// atributos, sin encabezado propio (la fila de categoría de ListPlegable ya
// es el único lugar donde aparece el nombre "Terreno").
import ListPlegable from "@/components/mapTools/layers/ListPlegable.vue";
import { mapService } from "@/services/map/mapService";
import { LayerService } from "@/services/layers/layerService.js";
import { createTerrainApi } from "@/services/terrain/api.js";
import { attributesTableService } from "@/services/layers/attributesTable.js";

// Capa_Terreno se sirve por GeoServer (PB-24_v2, Fase 2): un feature type
// propio por capa (terrain_features_layer_<terrainLayerId>), bajo el
// workspace/datastore compartidos "terreno"/"terrain_features_store"
// (aprovisionados una vez — ver services/terrain/geoserver_provisioning.go y
// thoughts/shared/plans/2026-07-25_PB-24_v2.md). Construir el descriptor acá
// mismo evita depender de un models.Layer real en Mongo — igual que hace
// cualquier capa geoserver.addLayerGeoserver, solo con estos valores fijos.
const TERRAIN_GEOSERVER_WORKSPACE = "terreno";
const terrainOlLayerId = (terrainLayerId) => `terrain:${terrainLayerId}`;
const terrainGeoserverDatastore = (terrainLayerId) => `terrain_features_layer_${terrainLayerId}`;
const terrainGeoserverDescriptor = (layer) => ({
  id: terrainOlLayerId(layer.terrainLayerId),
  name: layer.name,
  category_name: layer.category_name || layer.visualGroupName || "Terreno",
  orden: layer.orden || 0,
  provider: {
    name: "geoserver",
    url: process.env.VUE_APP_GEOSERVER_URL,
    geoserverdata: {
      workspace: TERRAIN_GEOSERVER_WORKSPACE,
      datastore: terrainGeoserverDatastore(layer.terrainLayerId),
    },
    // terrainFields (fieldId + label real de la pregunta, de GET
    // /maps/:mapId/section) es la marca que attributesTable.js usa para
    // saber que esta capa geoserver es en realidad una Capa_Terreno, y así
    // dar alias legibles a sus columnas — ver getGeoServerMetadata.
    terrainFields: layer.fields || [],
    // style (color/size/icon/opacity de "Estilo por mapa", tarea 13.1) —
    // layerService.js lo usa para armar el SLD_BODY del request WMS.
    // GeoServer no tiene ningún estilo propio asociado a este dato (vive
    // solo en Mongo), así que sin esto siempre se veía el estilo por
    // defecto del feature type, sin importar lo guardado.
    style: layer.style || null,
  },
});

export default {
  name: "TerrainSection",
  components: { ListPlegable },
  props: {
    filterType: {
      type: String,
      default: "all",
    },
    searchQuery: {
      type: String,
      default: "",
    },
    isSearchActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sectionLayers: [], // respuesta cruda de GET /maps/:mapId/section
      consumedCampaignQuery: null, // evita reactivar/reabrir tabla dos veces para el mismo ?terrainCampaignId
    };
  },
  computed: {
    mapId() {
      return this.$route.params.idMap;
    },
    // "Ver en el mapa" (TerrainCampaigns.vue::goToMap) navega con este query
    // param para que, al llegar, la capa de esa campaña se active y su
    // tabla de atributos se abra sola — acceso rápido sin tener que buscar
    // la capa manualmente en la lista.
    terrainCampaignQueryId() {
      return this.$route.query.terrainCampaignId || null;
    },
    terrainApi() {
      return createTerrainApi(this.$http, this.$store.getters.backendurl);
    },
    activeLayers() {
      return this.$store.getters.getActiveLayers || [];
    },
    activeTerrainLayerIds() {
      // provider.name ya es "geoserver" (Fase 3, PB-24_v2) — el id con
      // prefijo "terrain:" es la única marca estable de que una capa activa
      // es una Capa_Terreno, independiente de cómo la sirva el mapa.
      const ids = new Set();
      this.activeLayers.forEach((layer) => {
        if (typeof layer.id === "string" && layer.id.startsWith("terrain:")) ids.add(layer.terrainLayerId);
      });
      return ids;
    },
    // Agrupa por visualGroup — hoy siempre hay un único grupo "Terreno" (el
    // valor por defecto que crea la publicación de campaña), pero el modelo
    // ya soporta varios grupos administrados el día que exista la UI de
    // configuración (tarea 13.1, PUT /layers/:id/maps/:mapId).
    groupedCategories() {
      const groups = new Map();
      this.sectionLayers.forEach((entry) => {
        const groupId = entry.visualGroup.id;
        if (!groups.has(groupId)) {
          groups.set(groupId, { id: `terrain-group:${groupId}`, name: entry.visualGroup.name, layers: [] });
        }
        groups.get(groupId).layers.push({
          ...terrainGeoserverDescriptor({ terrainLayerId: entry.terrainLayerId, name: entry.layerName, fields: entry.fields, style: entry.style, visualGroupName: entry.visualGroup.name }),
          terrainLayerId: entry.terrainLayerId,
          fields: entry.fields,
          selected: this.activeTerrainLayerIds.has(entry.terrainLayerId),
          opacity: entry.style && entry.style.opacity != null ? entry.style.opacity : 1,
          // style top-level (no solo dentro de provider, que ya quedó del
          // spread de arriba): activateLayer()/onZoomLayer() vuelven a
          // llamar terrainGeoserverDescriptor(layer) sobre ESTE objeto (el
          // que arma el v-for de la plantilla), y esa función lee
          // layer.style top-level — igual que opacity un poco más arriba,
          // por la misma razón.
          style: entry.style || null,
        });
      });
      return Array.from(groups.values());
    },
    // Terreno vive fuera del catálogo tradicional, pero visualmente forma
    // parte de la misma lista. Debe respetar exactamente el mismo orden de
    // filtros: primero actividad y después búsqueda.
    filteredCategories() {
      let categories = this.groupedCategories;

      if (this.filterType === "active") {
        categories = categories
          .map((category) => ({
            ...category,
            layers: category.layers.filter((layer) => layer.selected),
          }))
          .filter((category) => category.layers.length > 0);
      }

      const query = this.searchQuery.toLowerCase().trim();
      if (!query) return categories;

      return categories
        .map((category) => {
          const categoryMatches = category.name.toLowerCase().includes(query);
          const matchingLayers = category.layers.filter((layer) =>
            layer.name.toLowerCase().includes(query)
          );

          if (!categoryMatches && matchingLayers.length === 0) return null;
          return {
            ...category,
            layers: categoryMatches ? category.layers : matchingLayers,
            isMatch: categoryMatches,
            hasMatchingChildren: matchingLayers.length > 0,
            autoExpand: true,
          };
        })
        .filter(Boolean);
    },
  },
  watch: {
    filteredCategories: {
      immediate: true,
      handler(categories) {
        const count = categories.reduce(
          (total, category) => total + category.layers.length,
          0
        );
        this.$emit("filtered-count", count);
      },
    },
    mapId: {
      immediate: true,
      handler() {
        this.loadSection();
      },
    },
    // Cubre el caso de navegar de "ver en el mapa" de una campaña a la de
    // otra campaña que apunta al MISMO mapa: el watcher de mapId no
    // vuelve a dispararse (el mapa no cambió), así que esto sí reacciona.
    terrainCampaignQueryId() {
      this.activateFromCampaignQuery();
    },
  },
  methods: {
    async loadSection() {
      if (!this.mapId) {
        this.sectionLayers = [];
        return;
      }
      try {
        const response = await this.terrainApi.getMapSection(this.mapId);
        this.sectionLayers = (response.data && response.data.layers) || [];
      } catch (error) {
        this.sectionLayers = [];
      }
      await this.activateFromCampaignQuery();
    },
    findSectionLayer(terrainLayerId) {
      for (const category of this.groupedCategories) {
        const found = category.layers.find((layer) => layer.terrainLayerId === terrainLayerId);
        if (found) return found;
      }
      return null;
    },
    // Resuelve ?terrainCampaignId a su terrainLayerId, activa esa capa si
    // hace falta, y abre su tabla de atributos — falla en silencio (el
    // usuario igual llega al mapa) si la campaña no existe o no tiene capa.
    async activateFromCampaignQuery() {
      const campaignId = this.terrainCampaignQueryId;
      if (!campaignId || campaignId === this.consumedCampaignQuery || !this.sectionLayers.length) return;
      try {
        const response = await this.terrainApi.getCampaign(campaignId);
        const terrainLayerId = response.data && response.data.terrainLayerId;
        const layer = terrainLayerId && this.findSectionLayer(terrainLayerId);
        if (!layer) return;
        this.consumedCampaignQuery = campaignId;
        // layer.selected sale del store de Vuex (activeLayers), que persiste
        // durante toda la sesión SPA — nadie llama nunca a la mutación
        // cleanActiveLayers al navegar entre mapas (existe pero está
        // huérfana). Si esta misma Capa_Terreno ya se activó antes en la
        // sesión (ej. se probó "Ver en el mapa" para esta campaña y no se
        // desactivó a mano), Vuex la sigue marcando "selected" aunque el
        // mapa OL actual nunca la haya recibido de verdad — activateLayer()
        // se saltaba por completo y el checkbox quedaba tildado sin ningún
        // punto en pantalla, hasta desactivar/activar a mano (lo que sí
        // fuerza un addLayerToMap real). mapService.layerExists reconcilia
        // contra el mapa real en vez de confiar solo en el store.
        if (!layer.selected || !mapService.layerExists(layer.id)) await this.activateLayer(layer);
        attributesTableService.openTable(layer);
      } catch (error) {
        // Campaña no resuelta (borrada, sin permisos, etc.) — sin acción.
      }
    },
    categoryCheckState(category) {
      const layers = category.layers || [];
      if (!layers.length) return "none";
      const activeCount = layers.filter((l) => l.selected).length;
      if (activeCount === 0) return "none";
      return activeCount === layers.length ? "all" : "some";
    },
    async activateLayer(layer) {
      const descriptor = terrainGeoserverDescriptor(layer);
      this.$store.dispatch("addActiveLayer", { ...descriptor, terrainLayerId: layer.terrainLayerId, opacity: layer.opacity });
      await LayerService.addLayerGeoserver(descriptor, null, true, "vector");
    },
    deactivateLayer(layer) {
      LayerService.removeLayerByID(layer.id);
      this.$store.dispatch("removeActiveLayer", { id: layer.id });
      if (this.$store.getters["attributeTable/layer"]?.id === layer.id) {
        this.$store.dispatch("attributeTable/closeTable");
      }
    },
    async onSelectLayer(category, layer) {
      if (!this.activeTerrainLayerIds.has(layer.terrainLayerId)) await this.activateLayer(layer);
    },
    onRemoveLayer(category, layer) {
      if (this.activeTerrainLayerIds.has(layer.terrainLayerId)) this.deactivateLayer(layer);
    },
    async onToggleCategory(category) {
      const allActive = (category.layers || []).every((l) => l.selected);
      for (const layer of category.layers || []) {
        if (allActive) this.deactivateLayer(layer);
        else if (!this.activeTerrainLayerIds.has(layer.terrainLayerId)) await this.activateLayer(layer);
      }
    },
    onUpdateOpacity(payload) {
      const { layer, opacity } = payload;
      mapService.setLayerOpacity(layer.id, opacity);
    },
    // No hace falta un handler propio para "Ver tabla de atributos":
    // ListPlegable llama directo a attributesTableService.openTable(layer)
    // (no emite un evento al padre), y ese servicio ya reconoce
    // provider:'geoserver' (attributesTable.js::getLayerMetadata/fetchGeoServer)
    // sin ninguna rama especial para Terreno — WFS DescribeFeatureType real.
    async onZoomLayer(category, layer) {
      if (!this.activeTerrainLayerIds.has(layer.terrainLayerId)) {
        // activateLayer ya hace zoom (LayerService.addLayerGeoserver con
        // doZoom=true por defecto) — no hace falta repetirlo acá.
        await this.activateLayer(layer);
        return;
      }
      // Capa ya activa: mismo mecanismo de zoom que cualquier otra capa
      // geoserver (WFS GetFeature de 1 registro por su bbox) — un TileWMS no
      // tiene un extent local que leer del lado del cliente como sí lo tenía
      // el VectorSource del sistema anterior.
      LayerService.getGeoserverExtent(terrainGeoserverDescriptor(layer));
    },
  },
};
</script>

<style scoped>
/* display:contents saca esta caja del árbol de layout — sus hijos (los
   ListPlegable) quedan visualmente como hermanos directos del contenedor
   real de Layers.vue, exactamente como cuando este componente no tenía
   raíz propia. */
.terrain-section-root { display: contents; }
</style>
